import {
    HttpClient,
    provideHttpClient,
    withFetch,
    withInterceptors,
    withInterceptorsFromDi,
} from '@angular/common/http';
import {
    APP_INITIALIZER,
    ApplicationConfig,
    LOCALE_ID,
    importProvidersFrom,
} from '@angular/core';
import { AngularFireModule } from '@angular/fire/compat';
import { AngularFireAnalyticsModule } from '@angular/fire/compat/analytics';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    NativeDateAdapter,
} from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
    PreloadAllModules,
    provideRouter,
    withInMemoryScrolling,
    withPreloading,
} from '@angular/router';
import { provideFuse } from '@fuse';
import { fuseLoadingInterceptor } from '@fuse/services/loading';

import { TranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { appRoutes } from 'app/app.routes';
import { provideAuth } from 'app/core/auth/auth.provider';
import { provideIcons } from 'app/core/icons/icons.provider';
import { environment } from 'environments/environment';
import { authABCApiInterceptor } from './core/auth-abc/auth-abc-api.interceptor';
import { networkInterceptor } from './core/interceptor/network.interceptor';
import { Analytics } from './core/services/analytics/analytics.service';
import { UserCountryService } from './core/services/user-country.service';
import { provideTenantConfig } from './core/tenant';
import { BunnyMultipartUploadService } from './shared/services/bunny-multipart-upload.service';

export function initializeAnalytics(analytics: Analytics): () => Promise<void> {
    return () => {
        // CleverTap is flag-gated and no-ops when disabled.
        analytics.initCleverTap();
        // Clarity is NOT initialized here — it is driven by user consent via
        // ConsentService/ConsentBannerComponent, so we never record a session
        // before the user has accepted.
        return Promise.resolve(); // Return a resolved promise to satisfy APP_INITIALIZER
    };
}

const httpLoaderFactory: (http: HttpClient) => TranslateHttpLoader = (
    http: HttpClient
) => {
    // Add cache-busting query parameter using build timestamp
    // This ensures translations are reloaded when the app is rebuilt/deployed
    // Format: .json?v=BUILD_TIMESTAMP (will be replaced at build time or use app version)
    // Add cache-busting for both staging and production environments
    // This ensures translations are reloaded when the app version changes
    // Prevents browser cache issues with translation files
    const cacheBuster = environment.production
        ? `?v=${environment.version || Date.now()}` // Use version in staging/production to prevent cache issues
        : ''; // In development, no cache-busting needed
    return new TranslateHttpLoader(http, './i18n/', `.json${cacheBuster}`);
};

export const appConfig: ApplicationConfig = {
    providers: [
        // Tenant config must resolve first — all interceptors and subsequent
        // initializers depend on the tenant's apiBaseUrl being available.
        provideTenantConfig(),

        provideAnimations(),
        provideHttpClient(
            withInterceptorsFromDi(),
            withInterceptors([
                authABCApiInterceptor,
                networkInterceptor,
                fuseLoadingInterceptor,
            ]),
            withFetch()
        ),
        // Firebase Compat providers for AngularFireAnalytics
        importProvidersFrom([
            AngularFireModule.initializeApp(environment.firebase),
            AngularFireAnalyticsModule,
        ]),
        provideTranslateService({
            loader: {
                provide: TranslateLoader,
                useFactory: httpLoaderFactory,
                deps: [HttpClient],
            },
        }),
        provideRouter(
            appRoutes,
            withPreloading(PreloadAllModules),
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' })
        ),
        {
            provide: APP_INITIALIZER,
            useFactory: initializeAnalytics, // Analytics (CleverTap, Clarity) init
            deps: [Analytics],
            multi: true,
        },

        // Material Date Adapter with DD/MM/YYYY format
        {
            provide: DateAdapter,
            useClass: NativeDateAdapter,
        },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: {
                    dateInput: ['dd/MM/yyyy', 'dd-MM-yyyy', 'dd.MM.yyyy'], // Accept DD/MM/YYYY formats
                },
                display: {
                    dateInput: 'dd/MM/yyyy', // Always display as DD/MM/YYYY
                    monthYearLabel: 'MMMM yyyy',
                    dateA11yLabel: 'dd/MM/yyyy',
                    monthYearA11yLabel: 'MMMM yyyy',
                },
            },
        },
        // Set locale to ensure DD/MM/YYYY format is used consistently
        {
            provide: LOCALE_ID,
            useValue: 'en-GB', // British English uses DD/MM/YYYY format
        },
        // Fuse
        provideAuth(),
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                services: [],
            },
            fuse: {
                layout: 'dense',
                scheme: 'light',
                screens: {
                    sm: '600px',
                    md: '960px',
                    lg: '1280px',
                    xl: '1440px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                    {
                        id: 'theme-brand',
                        name: 'Brand',
                    },
                    {
                        id: 'theme-teal',
                        name: 'Teal',
                    },
                    {
                        id: 'theme-rose',
                        name: 'Rose',
                    },
                    {
                        id: 'theme-purple',
                        name: 'Purple',
                    },
                    {
                        id: 'theme-amber',
                        name: 'Amber',
                    },
                ],
            },
        }),
        { provide: 'LOCALSTORAGE', useFactory: getLocalStorage },
        {
            provide: APP_INITIALIZER,
            useFactory: initializeCountryCode, // UserCountry initializes after
            deps: [UserCountryService],
            multi: true,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: (svc: BunnyMultipartUploadService) => () =>
                svc.cleanupExpiredStates(),
            deps: [BunnyMultipartUploadService],
            multi: true,
        },
    ],
};

export function getLocalStorage() {
    if (environment.production) {
        // Disable console.log in production
        window.console.log = () => {
            // Disable Log
        };
    }

    return typeof window !== 'undefined' ? window.localStorage : null;
}

export function initializeCountryCode(
    userCountryService: UserCountryService
): () => Promise<void> {
    return () => userCountryService.initialCountryCodeSetupByIp();
}
